import { Component, OnDestroy, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, takeWhile, tap } from 'rxjs/operators';
import * as fromDataServices from '../../data-services';
import * as fromLookups from '../../lookups';
import { CalcTypeService } from '../../lookups/calc-type.service';
import * as fromPayroll from '../../payroll/item/store';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { SelectItem } from '../../shared/efaact-primeng.module';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromAuths from '../../user-authorizations';
import * as fromUser from '../../user-info';
@Component({
    selector: 'ef-premium-pay-container',
    templateUrl: './premium-pay-container.component.html',
    styleUrls: ['./premium-pay-container.component.scss'],
    standalone: false
})
export class PremiumPayContainerComponent implements OnInit, OnDestroy {
    premiumPayService: EntityCollectionService<fromDataServices.PremiumPay.PremiumPay>;
    authorizations$: Observable<fromAuths.UserAuthorization[]>;
    userInfo$: Observable<fromUser.IUserInfo>;
    directReportsSelectItems$: Observable<SelectItem[]>;
    process$: Observable<fromShared.Process>;
    tasks$: Observable<fromLookups.ITask[]>;
    tasksSelectItems$: Observable<SelectItem[]>;
    alternateTasksSelectItems$: Observable<SelectItem[]>;
    payrollItems$: Observable<fromPayroll.PayrollItem[]>;
    payrollItemsSelectItems$: Observable<SelectItem[]>;
    costTypesSelectItems$: Observable<SelectItem[]>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    premiumPays$: Observable<fromDataServices.PremiumPay.PremiumPay[]>;
    TasksSubject = new ReplaySubject<SelectItem[]>(1);
    UsersSubject = new ReplaySubject<SelectItem[]>(1);
    PayrollItemSubject = new ReplaySubject<SelectItem[]>(1);
    ServiceItemSubject = new ReplaySubject<SelectItem[]>(1);
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    private active = true;
    constructor(
        public entityServices: EntityServices,
        private store: Store,
        private calcTypeService: CalcTypeService,
        private log: LoggingService
    ) {
        this.premiumPayService =
            entityServices.getEntityCollectionService<fromDataServices.PremiumPay.PremiumPay>(
                fromDataServices.PremiumPay.entityName
            );
    }

    ngOnInit(): void {
        this.setupObservables();
        this.setupSubscriptions();
        this.premiumPayService.getWithQuery({
            PPEndDate: moment().format('MM/DD/YYYY')
        });
        this.store.dispatch(new fromLookups.LoadTasks());
        this.store.dispatch(new fromLookups.LoadPayrollItems());
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors),
            tap(selected =>
                this.log.verbose({
                    type: 'PremiumPayContainerComponent:selectedError$',
                    selected
                })
            )
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'PremiumPayContainerComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            ),
            tap(selected =>
                this.log.verbose({
                    type: 'PremiumPayContainerComponent:errors$:filtered',
                    selected
                })
            )
        );
        this.authorizations$ = this.store.pipe(select(fromAuths.selectAll));
        this.tasks$ = this.store.pipe(select(fromLookups.getTasksLookup));
        this.tasksSelectItems$ = combineLatest([
            this.tasks$,
            this.authorizations$.pipe(
                filter(x => !!x && x.length > 0),
                map(auths => auths.filter(a => a.PremiumPayIndicator === true))
            )
        ]).pipe(
            tap(([tasks, auths]) =>
                this.log.verbose({
                    type: 'PremiumPayContainerComponent:authorizations$,tasks$',
                    tasks,
                    auths
                })
            ),
            map(([tasks, auths]) =>
                tasks.filter(
                    t =>
                        auths.findIndex(a => a.TaskListID === t.TaskListID) >=
                            0 && t.IsActive === true
                )
            ),
            map(x =>
                x
                    .map(y => ({
                        label: y.TaskLongName,
                        value: y.TaskListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.alternateTasksSelectItems$ = this.tasks$.pipe(
            map(x =>
                x
                    .map(y => ({
                        label: y.TaskLongName,
                        value: y.TaskListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.payrollItems$ = this.store.pipe(
            select(fromLookups.getPayrollItemsLookup),
            map(x => fromShared.uniqBy(x, y => y.PIListID)),
            map(x => x.filter(y => y.IsActive === true))
        );
        this.payrollItemsSelectItems$ = this.payrollItems$.pipe(
            map(x =>
                x
                    .map(y => ({
                        label: y.PayrollItem,
                        value: y.PIListID
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            )
        );

        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));

        this.directReportsSelectItems$ = this.store.pipe(
            select(fromLookups.getDirectReportsLookup),
            map(x => x.filter(y => y.IsActive === true)),
            map(x => clone(x)),
            map(x =>
                x.map(y => ({ label: y.EmployeeName, value: y.EmployeeListID }))
            ),
            map(reports =>
                reports.sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.costTypesSelectItems$ = this.calcTypeService
            .getCalcType()
            .pipe(
                map(ct =>
                    ct.map(x => ({ label: x.CalcDesc, value: x.CalcType }))
                )
            );
        this.premiumPays$ = combineLatest([
            this.premiumPayService.entities$.pipe(map(x => clone(x))),
            this.authorizations$
        ]).pipe(
            map(([pp, a]) =>
                pp.map(p => ({
                    ...p,
                    authorization: a.find(
                        x => x.EmpAuthID === p.EmpAuthID && x.EmpAuthEndDate
                    ) || {
                        EmpAuthStartDate: new Date(p.EmpAuthStartDate),
                        EmpAuthEndDate: new Date(p.EmpAuthEndDate)
                    }
                })),
            ),
            tap(items =>
                this.log.verbose({
                    type: 'PremiumPayContainerComponent:premiumPays$',
                    items
                })
            )
        );
    }
    setupSubscriptions() {
        this.userInfo$
            .pipe(
                takeWhile(() => this.active),
                tap(user =>
                    this.log.verbose({
                        type: 'PremiumPayContainerComponent:userInfo$',
                        user
                    })
                )
            )
            .subscribe(userInfo => {
                this.store.dispatch(
                    fromAuths.getUserAuthorizations({
                        endDate: new Date()
                    })
                );
                this.store.dispatch(
                    new fromLookups.LoadDirectReports({
                        user: userInfo,
                        activeOnly: false
                    })
                );
            });

        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        this.premiumPayService.entities$
            .pipe(takeWhile(() => this.active))
            .subscribe(x => {
                this.TasksSubject.next(
                    fromShared
                        .uniqBy(x, y => y.TaskLongName)
                        .map(p => ({
                            label: p.TaskLongName,
                            value: p.TaskLongName
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
                this.UsersSubject.next(
                    fromShared
                        .uniqBy(x, y => y.EmployeeName)
                        .map(p => ({
                            label: p.EmployeeName,
                            value: p.EmployeeName
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
                this.PayrollItemSubject.next(
                    fromShared
                        .uniqBy(x, y => y.PayrollItem)
                        .map(p => ({
                            label: p.PayrollItem,
                            value: p.PayrollItem
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
                this.ServiceItemSubject.next(
                    fromShared
                        .uniqBy(x, y => y.ServiceItem)
                        .map(p => ({
                            label: p.ServiceItem,
                            value: p.ServiceItem
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
            });
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    onFilter($event) {
        this.log.verbose({
            type: 'PremiumPayContainerComponent:onFilter',
            $event
        });
        this.premiumPayService.clearCache();
        this.premiumPayService.getWithQuery($event);
        this.store.dispatch(
            fromAuths.getUserAuthorizations({
                ...$event,
                endDate: $event.PPEndDate
                // directReports: [userInfo]
            })
        );
    }
    onSave($event) {
        this.log.verbose({
            type: 'PremiumPayContainerComponent:onSave',
            $event
        });
        this.premiumPayService.upsert(
            {
                ...$event,
                PremiumPay: $event.PremiumPay || 0,
                PremiumPercent: $event.PremiumPercent || 0
            },
            {
                isOptimistic: false
            }
        );
    }
    onDelete($event: fromDataServices.PremiumPay.PremiumPay[]) {
        this.log.verbose({
            type: 'PremiumPayContainerComponent:onDelete',
            $event
        });
        of($event.map(x => x.id))
            .pipe(
                takeWhile(() => this.active),
                tap(ids =>
                    this.log.verbose({
                        type: 'PremiumPayContainerComponent:onDelete:deleting',
                        ids
                    })
                ),
                map(ids =>
                    ids.map(id =>
                        this.premiumPayService.delete(id, {
                            isOptimistic: false
                        })
                    )
                )
            )
            .subscribe(
                resp =>
                    this.log.verbose({
                        type: 'PremiumPayContainerComponent:onDelete:subscribe',
                        resp
                    }),
                err =>
                    this.log.verbose({
                        type: 'PremiumPayContainerComponent:onDelete:subscribe',
                        err
                    })
            );
    }
    onShowError(event) {
        this.ShowError.next(event);
    }
}
